import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Cog, Filter, X, Edit } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductionView = () => {
  const [wos, setWos] = useState([]);
  const [filteredWos, setFilteredWos] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    wo_number: '',
    item_id: '',
    prod_date: '',
    machine_id: '',
    skill_id: '',
    status: '',
    plan_qty_min: '',
    plan_qty_max: ''
  });
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingWO, setEditingWO] = useState(null);
  const [editForm, setEditForm] = useState({
    wo_number: '',
    item_id: '',
    prod_date: '',
    plan_qty: '',
    machine_id: '',
    skill_id: ''
  });

  // Plan Qty change dialog state
  const [showPlanQtyDialog, setShowPlanQtyDialog] = useState(false);
  const [planQtyWO, setPlanQtyWO] = useState(null);
  const [planQtyForm, setPlanQtyForm] = useState({ new_qty: '', remarks: '' });
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [wos, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [woRes, itemRes, machRes, skillRes] = await Promise.all([
        axios.get(`${API}/production/orders`),
        axios.get(`${API}/items`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/skills`)
      ]);
      // Handle paginated response format
      const woData = woRes.data.data || woRes.data;
      setWos(Array.isArray(woData) ? woData : []);
      setItems(itemRes.data);
      setMachines(machRes.data);
      setSkills(skillRes.data);
    } catch (error) {
      console.error('Error fetching production data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...wos];

    if (filters.wo_number) {
      filtered = filtered.filter(w => w.wo_number.toLowerCase().includes(filters.wo_number.toLowerCase()));
    }

    if (filters.item_id && filters.item_id !== 'all') {
      filtered = filtered.filter(w => w.item_id === filters.item_id);
    }

    if (filters.prod_date) {
      filtered = filtered.filter(w => w.prod_date.includes(filters.prod_date));
    }

    if (filters.machine_id && filters.machine_id !== 'all') {
      filtered = filtered.filter(w => w.machine_id === filters.machine_id);
    }

    if (filters.skill_id && filters.skill_id !== 'all') {
      filtered = filtered.filter(w => w.skill_id === filters.skill_id);
    }

    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(w => w.status === filters.status);
    }

    if (filters.plan_qty_min) {
      filtered = filtered.filter(w => w.plan_qty >= parseFloat(filters.plan_qty_min));
    }

    if (filters.plan_qty_max) {
      filtered = filtered.filter(w => w.plan_qty <= parseFloat(filters.plan_qty_max));
    }

    setFilteredWos(filtered);
  };

  const clearFilters = () => {
    setFilters({
      wo_number: '',
      item_id: '',
      prod_date: '',
      machine_id: '',
      skill_id: '',
      status: '',
      plan_qty_min: '',
      plan_qty_max: ''
    });
  };

  const handleEditClick = async (wo) => {
    if (wo.status === 'COMPLETED') {
      toast.error('Cannot edit completed production orders');
      return;
    }
    
    setEditingWO(wo);
    setEditForm({
      wo_number: wo.wo_number,
      item_id: wo.item_id,
      prod_date: wo.prod_date,
      plan_qty: wo.plan_qty,
      machine_id: wo.machine_id || '',
      skill_id: wo.skill_id || ''
    });
    setShowEditDialog(true);
  };

  const handlePlanQtyClick = (wo) => {
    if (wo.status === 'COMPLETED') {
      toast.error('Cannot edit completed production orders');
      return;
    }
    setPlanQtyWO(wo);
    setPlanQtyForm({ new_qty: wo.plan_qty, remarks: '' });
    setShowPlanQtyDialog(true);
  };

  const handleSavePlanQty = async () => {
    if (!planQtyForm.remarks.trim()) {
      toast.error('Remarks is mandatory for plan qty changes');
      return;
    }
    if (parseInt(planQtyForm.new_qty) === planQtyWO.plan_qty) {
      toast.error('New quantity is same as current quantity');
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API}/production/orders/${planQtyWO.id}/plan-qty`, {
        new_qty: parseInt(planQtyForm.new_qty),
        remarks: planQtyForm.remarks.trim(),
        changed_by: user.username || user.name || 'admin'
      });
      toast.success('Plan quantity updated successfully');
      setShowPlanQtyDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan quantity');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = {
        id: editingWO.id,
        wo_number: editForm.wo_number,
        item_id: editForm.item_id,
        prod_date: editForm.prod_date,
        plan_qty: parseFloat(editForm.plan_qty),
        machine_id: editForm.machine_id || null,
        skill_id: editForm.skill_id || null,
        status: editingWO.status,
        good_qty: editingWO.good_qty || 0,
        scrap_qty: editingWO.scrap_qty || 0,
        created_ts: editingWO.created_ts
      };

      await axios.put(`${API}/production/orders/${editingWO.id}`, updateData);
      toast.success('Production order updated successfully');
      setShowEditDialog(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error updating production order:', error);
      toast.error(error.response?.data?.detail || 'Failed to update production order');
    }
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getMachineName = (id) => machines.find(m => m.id === id)?.name || 'N/A';
  const getSkillName = (id) => skills.find(s => s.id === id)?.name || 'N/A';

  const getStatusColor = (status) => {
    switch (status) {
      case 'PLANNED': return 'bg-blue-100 text-blue-800';
      case 'RUNNING': return 'bg-orange-100 text-orange-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const uniqueStatuses = ['PLANNED', 'RUNNING', 'COMPLETED'];

  return (
    <>
    <Card data-testid="production-orders-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>Production Work Orders</CardTitle>
              <CardDescription>In-house production orders with machine and skill assignments</CardDescription>
            </div>
          </div>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Section */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-sm text-slate-700">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">WO Number</label>
              <Input
                data-testid="filter-wo-number"
                placeholder="Search WO..."
                value={filters.wo_number}
                onChange={(e) => setFilters({ ...filters, wo_number: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Item</label>
              <Select
                data-testid="filter-item"
                value={filters.item_id}
                onValueChange={(value) => setFilters({ ...filters, item_id: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All items" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All items</SelectItem>
                  {items.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.part_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Production Date</label>
              <Input
                data-testid="filter-prod-date"
                type="date"
                value={filters.prod_date}
                onChange={(e) => setFilters({ ...filters, prod_date: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Status</label>
              <Select
                data-testid="filter-status"
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {uniqueStatuses.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Machine</label>
              <Select
                data-testid="filter-machine"
                value={filters.machine_id}
                onValueChange={(value) => setFilters({ ...filters, machine_id: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All machines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All machines</SelectItem>
                  {machines.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Skill</label>
              <Select
                data-testid="filter-skill"
                value={filters.skill_id}
                onValueChange={(value) => setFilters({ ...filters, skill_id: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All skills" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All skills</SelectItem>
                  {skills.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Plan Qty Min</label>
              <Input
                data-testid="filter-plan-qty-min"
                type="number"
                placeholder="Min"
                value={filters.plan_qty_min}
                onChange={(e) => setFilters({ ...filters, plan_qty_min: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Plan Qty Max</label>
              <Input
                data-testid="filter-plan-qty-max"
                type="number"
                placeholder="Max"
                value={filters.plan_qty_max}
                onChange={(e) => setFilters({ ...filters, plan_qty_max: e.target.value })}
                className="h-9"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : filteredWos.length === 0 ? (
          <div data-testid="no-wos" className="text-center py-8 text-slate-500">
            {hasActiveFilters ? 'No work orders match the selected filters.' : 'No work orders. Run production planning first.'}
          </div>
        ) : (
          <div>
            <div className="mb-2 text-sm text-slate-600">
              Showing <strong>{filteredWos.length}</strong> of <strong>{wos.length}</strong> records
            </div>
            <div className="overflow-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO Number</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Prod Date</TableHead>
                    <TableHead className="text-right">Plan Qty</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead>Skill</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWos.map((wo) => (
                    <React.Fragment key={wo.id}>
                    <TableRow data-testid={`wo-row-${wo.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {wo.wo_number}
                          {wo.wo_type === 'REWORK' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-200">REWORK</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{getItemInfo(wo.item_id)}</TableCell>
                      <TableCell>{wo.prod_date}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-semibold">{wo.plan_qty}</span>
                          {wo.status !== 'COMPLETED' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePlanQtyClick(wo)}
                              className="h-6 w-6 p-0"
                              title="Change Plan Quantity"
                              data-testid={`plan-qty-edit-${wo.id}`}
                            >
                              <Edit className="w-3 h-3 text-blue-600" />
                            </Button>
                          )}
                          {wo.qty_change_log?.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedLog(expandedLog === wo.id ? null : wo.id)}
                              className="h-6 px-1.5 py-0 text-[10px] text-amber-600 hover:text-amber-800"
                              title="View change history"
                              data-testid={`change-log-btn-${wo.id}`}
                            >
                              {wo.qty_change_log.length} log{wo.qty_change_log.length > 1 ? 's' : ''}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Cog className="w-4 h-4 text-slate-400" />
                          {getMachineName(wo.machine_id)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-purple-50">{getSkillName(wo.skill_id)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(wo.status)}>{wo.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {wo.status !== 'COMPLETED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(wo)}
                            className="gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Qty Change History */}
                    {expandedLog === wo.id && wo.qty_change_log?.length > 0 && (
                      <TableRow className="bg-amber-50/50">
                        <TableCell colSpan={7} className="py-2 px-6">
                          <div className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wider">Plan Qty Change History</div>
                          <table className="w-full text-xs border border-amber-200 rounded">
                            <thead>
                              <tr className="bg-amber-100/60 text-amber-800">
                                <th className="py-1.5 px-3 text-left">#</th>
                                <th className="py-1.5 px-3 text-left">Date & Time</th>
                                <th className="py-1.5 px-3 text-left">Changed By</th>
                                <th className="py-1.5 px-3 text-right">Old Qty</th>
                                <th className="py-1.5 px-3 text-right">New Qty</th>
                                <th className="py-1.5 px-3 text-left">Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wo.qty_change_log.map((log, idx) => (
                                <tr key={idx} className="border-t border-amber-100 hover:bg-amber-50" data-testid={`change-log-row-${wo.id}-${idx}`}>
                                  <td className="py-1.5 px-3 text-slate-500">{idx + 1}</td>
                                  <td className="py-1.5 px-3 text-slate-600">{log.changed_at ? new Date(log.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</td>
                                  <td className="py-1.5 px-3 font-medium text-slate-700">{log.changed_by}</td>
                                  <td className="py-1.5 px-3 text-right text-red-600">{log.old_qty?.toLocaleString()}</td>
                                  <td className="py-1.5 px-3 text-right text-green-600 font-semibold">{log.new_qty?.toLocaleString()}</td>
                                  <td className="py-1.5 px-3 text-slate-600 italic">{log.remarks}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Edit Production Order Dialog */}
    <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Production Order</DialogTitle>
          <DialogDescription>
            Modify production order details. Note: Cannot edit completed orders.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>WO Number</Label>
              <Input
                value={editForm.wo_number}
                onChange={(e) => setEditForm({ ...editForm, wo_number: e.target.value })}
                disabled
              />
            </div>
            <div>
              <Label>Production Date *</Label>
              <Input
                type="date"
                value={editForm.prod_date}
                onChange={(e) => setEditForm({ ...editForm, prod_date: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Item *</Label>
              <Select
                value={editForm.item_id}
                onValueChange={(value) => setEditForm({ ...editForm, item_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.part_number} - {item.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan Quantity</Label>
              <Input
                type="number"
                value={editForm.plan_qty}
                disabled
                className="bg-slate-100 cursor-not-allowed"
              />
              <p className="text-[10px] text-slate-400 mt-1">Use the pencil icon in Plan Qty column to change quantity with remarks</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Machine</Label>
              <Select
                value={editForm.machine_id}
                onValueChange={(value) => setEditForm({ ...editForm, machine_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select machine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {machines.map(machine => (
                    <SelectItem key={machine.id} value={machine.id}>
                      {machine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Skill</Label>
              <Select
                value={editForm.skill_id}
                onValueChange={(value) => setEditForm({ ...editForm, skill_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select skill" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {skills.map(skill => (
                    <SelectItem key={skill.id} value={skill.id}>
                      {skill.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowEditDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Plan Qty Change Dialog with Mandatory Remarks */}
    <Dialog open={showPlanQtyDialog} onOpenChange={setShowPlanQtyDialog}>
      <DialogContent className="max-w-md" data-testid="plan-qty-change-dialog">
        <DialogHeader>
          <DialogTitle>Change Plan Quantity</DialogTitle>
          <DialogDescription>
            {planQtyWO && <>WO: <span className="font-semibold text-slate-800">{planQtyWO.wo_number}</span> | Current Qty: <span className="font-bold text-blue-700">{planQtyWO.plan_qty}</span></>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div>
            <Label htmlFor="new_qty">New Plan Quantity *</Label>
            <Input
              id="new_qty"
              type="number"
              min="1"
              step="1"
              value={planQtyForm.new_qty}
              onChange={(e) => setPlanQtyForm({ ...planQtyForm, new_qty: e.target.value })}
              data-testid="plan-qty-new-input"
            />
          </div>
          <div>
            <Label htmlFor="remarks">Remarks * <span className="text-red-500 text-xs">(mandatory)</span></Label>
            <textarea
              id="remarks"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-none"
              placeholder="Enter reason for quantity change..."
              value={planQtyForm.remarks}
              onChange={(e) => setPlanQtyForm({ ...planQtyForm, remarks: e.target.value })}
              data-testid="plan-qty-remarks-input"
            />
          </div>

          {planQtyWO?.qty_change_log?.length > 0 && (
            <div className="border border-amber-200 rounded-md p-3 bg-amber-50/50">
              <div className="text-xs font-semibold text-amber-800 mb-2">Previous Changes ({planQtyWO.qty_change_log.length})</div>
              <div className="space-y-1 max-h-[120px] overflow-y-auto">
                {planQtyWO.qty_change_log.map((log, i) => (
                  <div key={i} className="text-[11px] text-slate-600 flex justify-between gap-2">
                    <span>{log.changed_at ? new Date(log.changed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</span>
                    <span className="text-red-500">{log.old_qty}</span>
                    <span>→</span>
                    <span className="text-green-600 font-semibold">{log.new_qty}</span>
                    <span className="italic truncate flex-1">{log.remarks}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowPlanQtyDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePlanQty}
            disabled={!planQtyForm.remarks.trim()}
            data-testid="plan-qty-save-btn"
          >
            Update Quantity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProductionView;
